import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import HalloweenBanner from "../components/HalloweenBanner";
import { api } from "../lib/api";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Zap,
  RefreshCw,
  Star,
  Lock,
  Package,
} from "lucide-react";
import { isOnSale, salePercentOff, saleCountdown } from "../lib/sale";

const HERO_BG_IMAGES = [
  "/api/static/hero-01-horde.png",
  "/api/static/hero-02-survivor.png",
  "/api/static/hero-03-chernarus.png",
  "/api/static/hero-04-mushroom.png",
  "/api/static/hero-05-biohazard-wall.png",
];

const CATEGORY_TILES = [
  {
    label: "Hoodies",
    href: "/shop?category=hoodie",
    testid: "cat-tile-hoodie",
    caption: "Heavyweight fleece",
    image: "https://images.pexels.com/photos/18956666/pexels-photo-18956666.jpeg",
  },
  {
    label: "Tees",
    href: "/shop?category=tee",
    testid: "cat-tile-tee",
    caption: "Ringspun cotton",
    image: "https://images.pexels.com/photos/26797746/pexels-photo-26797746.jpeg",
  },
  {
    label: "Coming Soon",
    href: "/coming-soon",
    testid: "cat-tile-coming-soon",
    caption: "Next release",
    image: "https://images.unsplash.com/photo-1523381210434-271e8be1f52b?crop=entropy&cs=srgb&fm=jpg&w=1200",
  },
];

const MARQUEE_ITEMS = [
  "LIMITED DROP · NEVER RESTOCKED",
  "FREE US SHIPPING OVER $150",
  "PDOB / STREET WEAR",
  "ABNORMAL STYLES CAPSULE · EST. 2026",
  "CUT SMALL. RELEASED ONCE.",
];

const HomePage = () => {
  const [featured, setFeatured] = useState([]);
  const [mysteryBoxes, setMysteryBoxes] = useState([]);
  const [bgIdx, setBgIdx] = useState(0);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [realReviews, setRealReviews] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { limited_edition: true } })
      .then((r) => setFeatured(r.data.filter((p) => p.category !== "mystery"))).catch(() => {});
    api.get("/products", { params: { category: "mystery" } })
      .then((r) => setMysteryBoxes(r.data)).catch(() => {});
    api.get("/reviews?limit=3").then((r) => setRealReviews(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    HERO_BG_IMAGES.forEach((src) => { const i = new Image(); i.src = src; });
    const id = setInterval(() => {
      setBgIdx((n) => (n + 1) % HERO_BG_IMAGES.length);
    }, 5500);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const anySale = featured.some((p) => isOnSale(p) && p.sale_ends_at);
    if (!anySale) return;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [featured]);

  return (
    <div className="min-h-screen bg-zinc-950">
      <HalloweenBanner />

      <NavBar />

      {/* HERO */}
      <section className="relative overflow-hidden border-b-2 border-zinc-800">
        <div className="absolute inset-0">
          {HERO_BG_IMAGES.map((src, i) => (
            <img
              key={src}
              src={src}
              alt=""
              aria-hidden="true"
              className={`absolute inset-0 w-full h-full object-cover grayscale contrast-125 transition-opacity duration-[1500ms] ease-in-out ${
                i === bgIdx ? "opacity-50 ken-burns" : "opacity-0"
              }`}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/40 via-zinc-950/70 to-zinc-950" />
          <div className="absolute inset-0 grit" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-40">
          <div className="max-w-3xl">
            <div className="font-mono-tech text-xs text-zinc-300 mb-4 tracking-[0.3em] animate-fade-in-up flex items-center">
              <span className="brand-accent-bar" />
              EST. 2020 · PDOB / STREET WEAR
            </div>
            <h1 className="display-h1 text-5xl sm:text-7xl md:text-8xl lg:text-9xl text-zinc-50 animate-fade-in-up" style={{ animationDelay: "80ms" }}>
              Drip.<br />
              <span className="text-zinc-400">Never</span> Restocked.
            </h1>
            <p
              className="mt-6 max-w-xl font-editorial text-sm sm:text-base text-zinc-400 leading-relaxed animate-fade-in-up"
              data-testid="hero-subline"
              style={{ animationDelay: "160ms" }}
            >
              Premium streetwear. Limited drops. Once gone, gone forever.
            </p>
            <div className="mt-10 flex flex-wrap gap-3 animate-fade-in-up" style={{ animationDelay: "240ms" }}>
              <Link
                to="/shop"
                data-testid="home-shop-cta"
                className="inline-flex items-center gap-2 bg-zinc-100 hover:bg-white text-zinc-950 px-7 py-3.5 font-display text-base tracking-widest uppercase transition-colors"
              >
                Shop All <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/shop?category=tee"
                data-testid="home-abnormal-cta"
                className="inline-flex items-center gap-2 btn-brand px-7 py-3.5 font-display text-base tracking-widest uppercase"
              >
                Abnormal Styles <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>

        {/* Ticker tape */}
        <div className="absolute bottom-0 inset-x-0 border-t border-zinc-800 bg-zinc-950/80 backdrop-blur overflow-hidden">
          <div className="marquee-track py-2 font-mono-tech text-[10px] uppercase tracking-[0.3em] text-zinc-400">
            {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((t, i) => (
              <span key={i} className="px-6 flex items-center gap-6">
                {t}
                <span className="w-1.5 h-1.5 bg-red-600 rounded-full" />
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-b border-zinc-800 bg-zinc-950" data-testid="trust-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 divide-x divide-zinc-800">
          {[
            { icon: Truck, title: "Free US Shipping", sub: "// on orders over $150" },
            { icon: ShieldCheck, title: "Secure Checkout", sub: "// PayPal encrypted" },
            { icon: Zap, title: "Limited Drops", sub: "// never restocked" },
            { icon: RefreshCw, title: "30-Day Returns", sub: "// unworn · original tags" },
          ].map(({ icon: Icon, title, sub }, i) => (
            <div key={i} className="flex items-center gap-3 py-6 px-4 sm:px-6 group">
              <div className="p-2 border border-zinc-700 text-zinc-200 shrink-0 group-hover:border-red-600 group-hover:text-red-500 transition-colors">
                <Icon className="w-4 h-4" />
              </div>
              <div>
                <div className="font-display text-sm text-zinc-50 uppercase tracking-widest leading-tight">{title}</div>
                <div className="font-mono-tech text-[10px] text-zinc-500 uppercase tracking-widest mt-0.5">{sub}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* MYSTERY BOX DROP */}
      {mysteryBoxes.length > 0 && (
        <section
          className="relative mystery-hero border-b border-zinc-800 overflow-hidden"
          data-testid="mystery-box-section"
        >
          <div className="absolute inset-0 grit opacity-40 pointer-events-none" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 relative">
            <div className="max-w-2xl mb-14">
              <div className="font-mono-tech text-[10px] tracking-[0.3em] text-red-500 mb-3 uppercase flex items-center gap-2">
                <Lock className="w-3 h-3" />
                Limited offer · What's inside stays inside
              </div>
              <h2 className="display-h2 text-5xl sm:text-6xl lg:text-7xl text-zinc-50">
                The Mystery <span className="text-red-600">Drop.</span>
              </h2>
              <p className="mt-5 text-zinc-400 text-base leading-relaxed max-w-xl">
                Three sealed tiers. Hand-packed. Never restocked once they're gone. Cut the tape and find out what's yours.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {mysteryBoxes
                .slice()
                .sort((a, b) => a.price - b.price)
                .map((box, i) => {
                  const soldOut = (box.stock ?? 0) <= 0;
                  const tier = ["DROP", "VAULT", "GRAIL"][i] || "TIER";
                  return (
                    <Link
                      key={box.id}
                      to={`/shop/${box.slug}`}
                      data-testid={`mystery-tier-${box.slug}`}
                      className="group relative bg-zinc-950 border border-zinc-800 hover:border-red-600 hover:mystery-glow transition-all duration-500"
                    >
                      <div className="aspect-square overflow-hidden bg-zinc-100 relative">
                        <img
                          src={box.image_url}
                          alt={box.name}
                          className={`w-full h-full object-cover transition-transform duration-700 ${
                            soldOut ? "grayscale opacity-50" : "group-hover:scale-105"
                          }`}
                        />
                        <div className="absolute top-3 left-3 flex items-center gap-2">
                          <span className="bg-red-600 text-zinc-50 font-mono-tech text-[10px] px-2 py-0.5 uppercase tracking-widest">
                            Tier {i + 1}
                          </span>
                          {!soldOut && (
                            <span className="bg-zinc-950/90 text-zinc-100 font-mono-tech text-[10px] px-2 py-0.5 uppercase tracking-widest">
                              {box.stock} left
                            </span>
                          )}
                        </div>
                        {soldOut && (
                          <div className="absolute inset-0 flex items-center justify-center bg-zinc-950/70">
                            <span className="border border-zinc-100 text-zinc-100 font-display text-xl px-4 py-1 tracking-[0.3em] uppercase">
                              Sold out
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="p-5 border-t border-zinc-800">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="font-mono-tech text-[10px] text-red-500 uppercase tracking-[0.3em]">
                              The {tier}
                            </div>
                            <div className="font-display text-2xl text-zinc-50 mt-1 uppercase tracking-tight">
                              {["Entry drop", "Sealed vault", "Grail crate"][i]}
                            </div>
                          </div>
                          <div className="font-display text-3xl text-zinc-50">${box.price.toFixed(0)}</div>
                        </div>
                        <div className="mt-4 flex items-center justify-between font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500 group-hover:text-red-400 transition-colors">
                          <span>Sealed · hand-packed</span>
                          <span className="flex items-center gap-1">Roll <ArrowRight className="w-3 h-3" /></span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
            </div>
          </div>
        </section>
      )}

      {/* SHOP BY CATEGORY */}
      <section className="border-b border-zinc-800" data-testid="category-tiles-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="mb-10 flex items-end justify-between gap-4">
            <div>
              <div className="font-mono-tech text-[10px] tracking-[0.3em] text-zinc-500 mb-3 uppercase flex items-center">
                <span className="brand-accent-bar" />
                Shop by category
              </div>
              <h2 className="display-h2 text-4xl sm:text-5xl text-zinc-50">Pick your fit.</h2>
            </div>
            <Link
              to="/shop"
              className="hidden sm:inline-flex font-mono-tech text-xs uppercase tracking-widest text-zinc-400 hover:text-zinc-100 items-center gap-1 link-underline"
              data-testid="home-shop-link"
            >
              Browse all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {CATEGORY_TILES.map((c) => (
              <Link
                key={c.href}
                to={c.href}
                data-testid={c.testid}
                className={`group relative aspect-[3/4] overflow-hidden bg-zinc-900 transition-all duration-500 ${
                  c.accent ? "ring-1 ring-red-600/50 hover:ring-red-500" : ""
                }`}
              >
                {c.image && (
                  <img
                    src={c.image}
                    alt=""
                    aria-hidden="true"
                    className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ${
                      c.accent
                        ? "opacity-70 group-hover:opacity-90 group-hover:scale-105"
                        : "grayscale opacity-50 group-hover:opacity-80 group-hover:scale-105"
                    }`}
                  />
                )}
                <div className={`absolute inset-0 bg-gradient-to-t ${
                  c.accent ? "from-red-950/80" : "from-zinc-950"
                } via-zinc-950/70 to-transparent`} />
                <div className="absolute inset-x-0 bottom-0 p-4">
                  <div className="font-display text-2xl text-zinc-50 uppercase tracking-tight leading-none">
                    {c.label}
                  </div>
                  <div className={`mt-1.5 font-mono-tech text-[10px] uppercase tracking-widest ${
                    c.accent ? "text-red-400" : "text-zinc-500"
                  }`}>
                    {c.caption}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* LIMITED EDITION */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="featured-section">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.3em] text-zinc-500 mb-3 uppercase flex items-center">
              <span className="brand-accent-bar" />
              Drop 002 — limited edition
            </div>
            <h2 className="display-h2 text-4xl sm:text-5xl text-zinc-50">Limited drop.</h2>
          </div>
          <Link to="/shop" className="font-mono-tech text-xs uppercase text-zinc-400 hover:text-zinc-100 flex items-center gap-1 link-underline" data-testid="home-view-all-link">
            View all <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((p) => {
            const onSale = isOnSale(p);
            const cd = saleCountdown(p, nowMs);
            const pct = salePercentOff(p);
            const soldOut = (p.stock ?? 0) <= 0 || !!p.sold_out;
            return (
            <Link
              to={`/shop/${p.slug}`}
              key={p.id}
              data-testid={`featured-product-${p.slug}`}
              className="group bg-zinc-950 transition-all duration-300 relative"
            >
              {onSale && !soldOut && (
                <span className="absolute top-2 right-2 z-10 bg-red-600 text-zinc-50 font-mono-tech text-[10px] px-2 py-1 uppercase tracking-widest" data-testid={`home-sale-badge-${p.slug}`}>
                  {pct > 0 ? `-${pct}%` : "SALE"}
                </span>
              )}
              <div className="aspect-square bg-zinc-900 overflow-hidden relative">
                <img
                  src={p.image_url}
                  alt={p.name}
                  className={`w-full h-full object-cover transition-transform duration-500 ${
                    soldOut ? "grayscale opacity-50 blur-[7px] scale-105" : "group-hover:scale-105"
                  }`}
                />
                {soldOut && (
                  <div className="absolute inset-0 flex items-center justify-center bg-zinc-950/55 pointer-events-none" data-testid={`home-sold-out-${p.slug}`}>
                    <div className="w-[115%] bg-zinc-950/95 border-y-2 border-red-600 py-3 -rotate-[8deg] shadow-[0_20px_60px_-10px_rgba(220,38,38,0.7)] text-center">
                      <div className="font-display text-2xl sm:text-3xl text-red-500 tracking-[0.35em] uppercase leading-none">Sold Out</div>
                      <div className="font-mono-tech text-[9px] text-zinc-400 tracking-[0.4em] uppercase mt-1">// Restock Unavailable</div>
                    </div>
                  </div>
                )}
                {cd && !soldOut && (
                  <span
                    className="absolute bottom-2 right-2 bg-zinc-950/85 text-red-400 font-mono-tech text-[10px] px-2 py-0.5 uppercase tracking-widest"
                    data-testid={`home-sale-countdown-${p.slug}`}
                  >
                    Ends in {cd}
                  </span>
                )}
              </div>
              <div className="pt-3 flex justify-between items-start">
                <div>
                  <div className="font-mono-tech text-[10px] text-zinc-500 uppercase tracking-widest">{p.category}</div>
                  <div className="font-display text-base text-zinc-50 mt-1 uppercase tracking-wider">{p.name}</div>
                </div>
                <div className="font-mono-tech text-sm text-right whitespace-nowrap pt-1">
                  {soldOut ? (
                    <span className="text-zinc-600 line-through">${p.price.toFixed(2)}</span>
                  ) : onSale ? (
                    <>
                      <span className="text-zinc-500 line-through mr-2">${p.price.toFixed(2)}</span>
                      <span className="text-red-500">${Number(p.sale_price).toFixed(2)}</span>
                    </>
                  ) : (
                    <span className="text-zinc-200">${p.price.toFixed(2)}</span>
                  )}
                </div>
              </div>
            </Link>
            );
          })}
          {featured.length === 0 && (
            <div className="col-span-full text-center py-12 font-mono-tech text-zinc-500 uppercase text-sm">// No limited drops live right now. Check back soon.</div>
          )}
        </div>
      </section>

      {/* CUSTOMER REVIEWS */}
      {realReviews.length > 0 && (
        <section className="border-y border-zinc-800 bg-zinc-950" data-testid="testimonials-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
            {(() => {
              const hero = {
                name: realReviews[0].name,
                loc: realReviews[0].location || "",
                quote: realReviews[0].quote,
                rating: realReviews[0].rating,
              };
              return (
                <>
                  <div className="flex items-center justify-center gap-1 mb-6" data-testid="home-reviews-aggregate">
                    {Array.from({ length: hero.rating }).map((_, k) => (
                      <Star key={k} className="w-4 h-4 fill-red-500 text-red-500" />
                    ))}
                  </div>
                  <blockquote
                    className="display-h2 text-2xl sm:text-3xl md:text-4xl text-zinc-50 max-w-3xl mx-auto"
                    data-testid="review-hero-quote"
                  >
                    &ldquo;{hero.quote}&rdquo;
                  </blockquote>
                  <div className="mt-8 font-mono-tech text-xs text-zinc-500 uppercase tracking-widest">
                    {hero.name}{hero.loc ? ` — ${hero.loc}` : ""}
                  </div>
                  <div className="mt-10 flex flex-wrap justify-center gap-3">
                    <Link
                      to="/reviews"
                      className="font-mono-tech text-xs uppercase tracking-widest px-5 py-2.5 border border-zinc-700 hover:border-zinc-300 text-zinc-300 hover:text-zinc-100 transition-colors"
                      data-testid="home-all-reviews-link"
                    >
                      Read all reviews
                    </Link>
                    <Link
                      to="/reviews#write"
                      className="font-mono-tech text-xs uppercase tracking-widest px-5 py-2.5 text-zinc-400 hover:text-zinc-100 flex items-center gap-1 link-underline"
                      data-testid="home-write-review-link"
                    >
                      Write a review <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </>
              );
            })()}
          </div>
        </section>
      )}

      {/* MANIFESTO */}
      <section className="border-y border-zinc-800 bg-zinc-950" data-testid="brand-story-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="font-mono-tech text-[10px] tracking-[0.3em] text-zinc-500 mb-4 uppercase">
            Est. 2020 · Los Angeles, CA
          </div>
          <h2 className="display-h2 text-3xl sm:text-4xl md:text-5xl text-zinc-50">
            Independent apparel house.<br />
            <span className="text-zinc-400">Cut small. Released once. Never restocked.</span>
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-4 items-center">
            <Link
              to="/shop"
              data-testid="brand-shop-link"
              className="inline-flex items-center gap-2 bg-zinc-100 hover:bg-white text-zinc-950 px-6 py-3 font-display text-sm tracking-widest uppercase transition-colors"
            >
              Shop the line <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact#about"
              className="font-mono-tech text-xs tracking-widest uppercase text-zinc-400 hover:text-zinc-100 link-underline"
            >
              Read the story
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
